import React, { PropsWithChildren, useEffect, useState } from 'react'
import { intervalToDuration, isBefore } from 'date-fns'
import i18next from 'i18next'
import { I18nextProvider, initReactI18next } from 'react-i18next'
import merge from 'lodash/merge'
import { useBranding } from '../branding'

import { makeTranslations } from './util'
import rawTranslations from './translations'

const baseTranslations = makeTranslations(rawTranslations)

async function createTranslationProvider(translationOverrides: Record<string, any> | undefined) {
  const translations = merge({}, baseTranslations, translationOverrides)

  const i18n = i18next
    .createInstance({
      fallbackLng: 'en',
      debug: false,

      interpolation: {
        escapeValue: false,
        skipOnVariables: false,
      },

      resources: {
        en: {
          translation: translations.en,
        },
        de: {
          translation: translations.de,
        },
      },

      react: {
        transKeepBasicHtmlNodesFor: ['p', 'br', 'strong', 'i', 'b'],
        transSupportBasicHtmlNodes: true,
        useSuspense: false,
      },
    })
    .use(initReactI18next)

  await i18n.init()

  i18n.services.formatter?.addCached('daterange', (lng, _options) => {
    const formatter = new Intl.DateTimeFormat(lng!, { dateStyle: 'short' })
    return (val) => {
      const { start, end } = val
      if (isBefore(end, start)) {
        return `${formatter.format(start)} - ${formatter.format(end)}`
      }
      return formatter.formatRange(start, end)
    }
  })
  i18n.services.formatter?.addCached('timerange', (lng, _options) => {
    const formatter = new Intl.DateTimeFormat(lng!, { timeStyle: 'short' })
    return (val) => {
      const { start, end } = val
      if (isBefore(end, start)) {
        return `${formatter.format(start)} - ${formatter.format(end)}`
      }
      return (
        formatter
          .formatRange(start, end)
          // Browsers format time ranges in German as "8:00-9:00 Uhr" which we don't want.
          .replaceAll(/\s*Uhr\s*/g, '')
      )
    }
  })
  i18n.services.formatter?.addCached('datetimerange', (lng, _options) => {
    const formatter = new Intl.DateTimeFormat(lng!, { dateStyle: 'short', timeStyle: 'short' })
    return (val) => {
      const { start, end } = val
      if (isBefore(end, start)) {
        return `${formatter.format(start)} - ${formatter.format(end)}`
      }
      return formatter.formatRange(start, end)
    }
  })
  i18n.services.formatter?.addCached('duration', (_lng, _options) => {
    return (val) => {
      let { seconds, minutes, hours } = val
      let totalSeconds = (seconds || 0) + (minutes || 0) * 60 + (hours || 0) * 3600
      hours = Math.floor(totalSeconds / 3600)
      totalSeconds -= hours * 3600
      minutes = Math.floor(totalSeconds / 60)
      seconds = Math.round(totalSeconds - minutes * 60)
      return (
        [hours > 0 ? `${hours}h` : null, minutes > 0 ? `${minutes}m` : null, seconds > 0 ? `${seconds}s` : null]
          .filter(Boolean)
          .join(' ') || '-'
      )
    }
  })
  i18n.services.formatter?.addCached('relativedate', (lng, _options) => {
    const formatter = new Intl.RelativeTimeFormat(lng!, { style: 'short', numeric: 'auto' })
    return (dueDate) => {
      const now = new Date()
      const [start, end, reversed] = isBefore(now, dueDate) ? [now, dueDate, false] : [dueDate, now, true]

      const d = intervalToDuration({ start, end })

      let unit
      if (d.years! > 2) {
        unit = 'years'
      } else {
        d.months = (d.months || 0) + 12 * (d.years || 0)
        if (d.months >= 3) {
          unit = 'months'
        } else {
          d.days = (d.days || 0) + Math.round(30.5 * (d.months || 0))
          if (d.days >= 13) {
            unit = 'weeks'
            d.weeks = Math.round(d.days / 7)
          } else {
            if (d.days >= 2) {
              unit = 'days'
            } else {
              d.hours = (d.hours || 0) + 24 * (d.days || 0)
              if (d.hours >= 3) {
                unit = 'hours'
              } else {
                d.minutes = (d.minutes || 0) + 60 * (d.hours || 0)
                if (d.minutes >= 5) {
                  unit = 'minutes'
                } else {
                  unit = 'seconds'
                  d.seconds = (d.seconds || 0) + 60 * (d.minutes || 0)
                }
              }
            }
          }
        }
      }
      return formatter.format((reversed ? -1 : 1) * ((d as any)[unit] || 0), unit as any)
    }
  })
  i18n.services.formatter?.addCached('language', (lng, _options) => {
    const formatter = new Intl.DisplayNames([lng!], { type: 'language' })
    return (code) => {
      return formatter.of(code.replace(/-.*/, '')) || code
    }
  })
  i18n.services.formatter?.addCached('bytes', (_lng, _options) => {
    return (size) => {
      const s = parseInt(size, 10)
      if (Number.isNaN(s)) {
        return '?'
      }
      if (s >= 1024 * 1024 * 1024 * 1024) {
        return `${(s / (1024 * 1024 * 1024 * 1024)).toFixed(2)} tb`
      }
      if (s >= 1024 * 1024 * 1024) {
        return `${(s / (1024 * 1024 * 1024)).toFixed(2)} gb`
      }
      if (s >= 1024 * 1024) {
        return `${(s / (1024 * 1024)).toFixed(2)} mb`
      }
      if (s >= 1024) {
        return `${(s / 1024).toFixed(2)} kb`
      }
      return String(size) + ' b'
    }
  })

  return ({ children }: React.PropsWithChildren) => React.createElement(I18nextProvider, { i18n }, children)
}

export default React.memo(function TranslationProvider({ children }: PropsWithChildren) {
  const { translations } = useBranding()

  const [ProviderComponent, setProviderComponent] = useState<React.FC>()
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const provider = await createTranslationProvider(translations)
      if (!cancelled) {
        setProviderComponent(() => provider)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [translations])
  if (!ProviderComponent) {
    return null
  }
  return React.createElement(ProviderComponent, {}, children)
})
